/**
 * InitializeDrawPanel
 *
 * Rendered at the top of the rodeo's draw management surface. Only
 * shown while the rodeo is still in sandbox mode:
 *
 *   1. `draw_mode === 'sandbox'` — shows a yellow "Sandbox / test
 *      mode" banner explaining that delegates can run draws but
 *      publish/finalize/notify are locked. Includes an "Initialize
 *      Draw" CTA that opens a confirmation modal; on confirm, runs
 *      the ferris-wheel rotation that:
 *        - shifts every association member up one group (G4→G1,
 *          G1→G2, G2→G3, G3→G4)
 *        - randomly draws a new position within the new group
 *        - persists an audit snapshot + flips draw_mode to 'live'
 *
 *   2. `draw_mode === 'live'` — the panel renders nothing. Once the
 *      ferris wheel has been initialized the page header's status
 *      badge + the Manage Draws list card already convey the live
 *      state, so a persistent "Draw is live" banner just takes up
 *      vertical space and reads to admins as a stale notification.
 *      The View Initialization History action stays accessible from
 *      the Manage Draws list card.
 */

import { Fragment, useState } from 'react';
import {
  AlertTriangle,
  Loader2,
  Play,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/contexts/ToastContext';
import {
  useDrawMode,
  useInitializeDraw,
} from '@/api/extensions/useDrawInitialization';

interface InitializeDrawPanelProps {
  rodeoId: string;
}

export function InitializeDrawPanel({ rodeoId }: InitializeDrawPanelProps) {
  const { data: status, isLoading, isError } = useDrawMode(rodeoId);
  const initialize = useInitializeDraw();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { showSuccess, showError } = useToast();

  if (isLoading) {
    return (
      <div className="rounded-lg border border-border bg-card p-4">
        <Skeleton className="h-5 w-48" />
        <Skeleton className="mt-2 h-4 w-72" />
      </div>
    );
  }

  if (isError || !status) {
    return null;
  }

  const isSandbox = status.draw_mode === 'sandbox';
  // Live mode is fully gated on the server-truth `draw_mode` field — once
  // the initialize mutation invalidates this query and the refetch lands
  // with `draw_mode: 'live'` we render nothing. The Live badge in the
  // page header + the per-rodeo card on the Manage Draws list already
  // communicate the live state, and the audit history is still reachable
  // from that card's "View initialization history" action.
  if (!isSandbox) {
    return null;
  }

  const handleInitialize = async () => {
    try {
      const result = await initialize.mutateAsync({ rodeoId });
      // A membership-less association rotates nobody — say that plainly
      // instead of "rotated 0 members", which reads like a failure.
      const rotatedStr =
        result.member_count === 0
          ? 'This association has no members on the priority list, so there was nothing to rotate.'
          : `Ferris wheel rotated ${result.member_count} member${result.member_count === 1 ? '' : 's'}.`;
      const resetStr =
        result.draft_draws_reset > 0
          ? ` ${result.draft_draws_reset} sandbox draft draw${result.draft_draws_reset === 1 ? ' was' : 's were'} cleared so re-runs use the new priorities.`
          : '';
      showSuccess(
        'Draw initialized',
        `${rotatedStr} The draw is now live.${resetStr}`,
      );
      setConfirmOpen(false);
    } catch (err: unknown) {
      const e = err as { code?: string; message?: string };
      const friendly =
        e?.code === 'ALREADY_LIVE'
          ? 'This rodeo is already in live mode.'
          : e?.code === 'PARTIALLY_INITIALIZED'
            ? 'A prior attempt left this rodeo in a half-initialized state. Manual cleanup is required before retrying — see the error detail below.'
            : (e?.message ?? 'Unknown error');
      showError('Initialization failed', friendly);
    }
  };

  return (
    <Fragment>
      <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 dark:border-amber-900/60 dark:bg-amber-950/30">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-700 dark:text-amber-300" />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-100">
                Draw is in sandbox / test mode
              </h3>
              <Badge
                variant="outline"
                className="border-amber-400/60 bg-amber-100/70 text-amber-900 dark:border-amber-700/60 dark:bg-amber-900/40 dark:text-amber-100"
              >
                Sandbox
              </Badge>
            </div>
            <p className="mt-1 text-sm leading-relaxed text-amber-800/90 dark:text-amber-200/90">
              You can run draws here to test placements, but{' '}
              <strong>lock, finalize, and send-notifications are
              disabled</strong>{' '}
              until the ferris wheel has been initialized for this rodeo.
              Initializing rotates every association member through the
              4-group ferris wheel (G4→G1, G1→G2, G2→G3, G3→G4) and
              assigns each member a random new position inside their new
              group. The rotation runs exactly once per rodeo and is
              captured in an audit snapshot.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button
                size="sm"
                onClick={() => setConfirmOpen(true)}
                disabled={initialize.isPending}
                className="bg-amber-700 text-white hover:bg-amber-800 dark:bg-amber-700 dark:hover:bg-amber-600"
              >
                {initialize.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Initializing…
                  </>
                ) : (
                  <>
                    <Play className="mr-2 h-4 w-4" />
                    Initialize draw (run ferris wheel)
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <ConfirmInitializeModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleInitialize}
        isRunning={initialize.isPending}
      />
    </Fragment>
  );
}

interface ConfirmInitializeModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isRunning: boolean;
}

function ConfirmInitializeModal({
  open,
  onClose,
  onConfirm,
  isRunning,
}: ConfirmInitializeModalProps) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !isRunning && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Play className="h-5 w-5" />
            Initialize ferris-wheel rotation?
          </DialogTitle>
          <DialogDescription>
            This rotates every association member through the 4-group
            ferris wheel one time, draws each member a random new
            position inside their new group, and flips the draw to
            <strong> live</strong> mode (enabling lock, finalize,
            and notifications for this rodeo).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm text-muted-foreground">
          <p>
            <strong>This cannot be undone</strong> via the normal admin
            flow. If positions are wrong afterwards, individual members
            can still be moved on the Ferris Wheel page.
          </p>
          <p>
            Any sandbox-mode <strong>draft draws will be cleared</strong>{' '}
            so re-runs use the new shifted priorities. Locked or
            finalized draws are never touched.
          </p>
          <p>
            An audit snapshot of every member's prior → new position
            will be saved and viewable on this page.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isRunning}>
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={isRunning}>
            {isRunning ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Running…
              </>
            ) : (
              'Initialize draw'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

